import { useEffect, useRef } from 'react';

/**
 * useModalA11y — accessibility plumbing for modal / overlay dialogs.
 *
 * Provides:
 *  - focus trap (Tab / Shift+Tab cycle within the container)
 *  - initial focus into the modal
 *  - focus restore to the previously-focused element on close
 *  - Escape-to-close parity
 *
 * Usage:
 *   const ref = useRef(null);
 *   useModalA11y({ isOpen, onClose, containerRef: ref });
 *   return <div ref={ref} role="dialog" aria-modal="true">...</div>;
 *
 * @param {boolean}  isOpen
 * @param {Function} onClose        called on Escape / requested close
 * @param {React.RefObject} containerRef  ref to the modal container element
 * @param {boolean} [closeOnEscape=true]
 */
export default function useModalA11y({ isOpen, onClose, containerRef, closeOnEscape = true }) {
    const prevFocusRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return undefined;

        // Remember what had focus so we can restore it on close.
        prevFocusRef.current = document.activeElement;

        const container = containerRef?.current;

        const getFocusable = () => {
            if (!container) return [];
            return Array.from(
                container.querySelectorAll(
                    'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
                )
            ).filter((el) => el.offsetParent !== null || el === document.activeElement);
        };

        // Move focus into the modal.
        const focusables = getFocusable();
        if (focusables.length > 0) {
            focusables[0].focus();
        } else if (container && typeof container.focus === 'function') {
            container.focus();
        }

        const onKeyDown = (e) => {
            if (e.key === 'Escape' && closeOnEscape) {
                e.preventDefault();
                onClose?.();
                return;
            }
            if (e.key === 'Tab') {
                const items = getFocusable();
                if (items.length === 0) return;
                const first = items[0];
                const last = items[items.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', onKeyDown, true);

        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            const prev = prevFocusRef.current;
            if (prev && typeof prev.focus === 'function') {
                try { prev.focus(); } catch (_) { /* element removed */ }
            }
        };
    }, [isOpen, onClose, containerRef, closeOnEscape]);
}
